import React from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faKey, faShieldHalved } from "@fortawesome/pro-solid-svg-icons";

import { Pill, PillVariants } from "src/design-system/Pill";
import { Tooltip } from "src/design-system/Tooltip";
import {
  BYOK_TOOLTIP,
  REGION_BADGES,
  TAKTILE_HOSTED_TOOLTIP,
} from "src/genai/ModelSelector/constants";
import { ModelOption } from "src/genai/ModelSelector/types";

type ResidencyPillProps = {
  icon: IconProp;
  label: string;
  variant: PillVariants;
  tooltip?: React.ReactNode;
  /** Prominent tooltip heading. Surfaces the full label when the pill truncates it. */
  tooltipTitle?: React.ReactNode;
  /** Cap the pill width and truncate the label; the full label stays in the tooltip title. */
  truncate?: boolean;
};

const ResidencyPill: React.FC<ResidencyPillProps> = ({
  icon,
  label,
  variant,
  tooltip,
  tooltipTitle,
  truncate = false,
}) => {
  const pill = (
    <Pill size="sm" variant={variant} maxWidth={truncate}>
      <Pill.Icon icon={icon} />
      <Pill.Text>{label}</Pill.Text>
    </Pill>
  );

  // Truncation needs a width-bounded parent; the bare pill sizes to its content.
  const trigger = truncate ? <div className="flex max-w-40">{pill}</div> : pill;

  if (!tooltip && !tooltipTitle) return trigger;

  return (
    <Tooltip body={tooltip} title={tooltipTitle} placement="top" asChild>
      {trigger}
    </Tooltip>
  );
};

/**
 * The BYOK deployment badge. Shows the credential name so multiple keys are
 * distinguishable at a glance, falling back to "Your key" when the deployment
 * carries no label. A long name is capped and truncated in the pill, with the
 * full name available in the tooltip.
 */
export const ByokBadge: React.FC<{ model: ModelOption }> = ({ model }) =>
  model.byok ? (
    <ResidencyPill
      icon={faKey}
      label={model.deploymentLabel ?? "Your key"}
      tooltip={BYOK_TOOLTIP}
      tooltipTitle={model.deploymentLabel}
      truncate={model.deploymentLabel !== undefined}
      variant="outlined-white"
    />
  ) : null;

/**
 * The residency-slot badges: Taktile-hosted + region for managed models. BYOK
 * deployments route through the customer's own provider, so their residency
 * isn't ours to assert: the customer-key badge stands in instead.
 */
export const ResidencyBadges: React.FC<{ model: ModelOption }> = ({
  model,
}) => {
  if (model.byok) return <ByokBadge model={model} />;

  const regionBadge = model.region ? REGION_BADGES[model.region] : undefined;

  return (
    <>
      {regionBadge && (
        <ResidencyPill
          icon={regionBadge.icon}
          label={regionBadge.label}
          variant={regionBadge.variant}
        />
      )}
      {model.hosting === "aws" && (
        <ResidencyPill
          icon={faShieldHalved}
          label="Taktile"
          tooltip={TAKTILE_HOSTED_TOOLTIP}
          variant="indigo-light"
        />
      )}
    </>
  );
};
